import {
  Button,
  Field,
  IconButton,
  Input,
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseTrigger,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalPositioner,
  ModalTitle,
  Text,
} from "@commonsku/design-system";
import { XIcon } from "@commonsku/styles";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { createCustomDomain } from "../../../../actions/customDomain";

interface CustomDomainInputPopupProps {
  onSubmit: () => void;
  onClose: () => void;
  shop_id: string;
}

const domainRegex =
  /^(https?:\/\/)?([a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/i;

const CustomDomainInputPopup = ({
  onSubmit,
  onClose,
  shop_id,
}: CustomDomainInputPopupProps) => {
  const dispatch = useDispatch();
  const [domain, setDomain] = useState("");
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const isDomainValid = domainRegex.test(domain);
  const isInvalid = showError && domain.length > 0 && !isDomainValid;
  const showErrorMessage = isInvalid || Boolean(requestError);

  const handleSubmit = async (domain: string) => {
    if (isDomainValid && !isLoading) {
      setIsLoading(true);
      setRequestError(null);
      try {
        await dispatch(createCustomDomain(shop_id, domain));
        onSubmit();
      } catch (err: any) {
        const message =
          err?.json?.errors?.[0] || "Something went wrong. Please try again.";
        setRequestError(message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Modal
      closeOnEscape
      closeOnInteractOutside
      modal
      motionPreset="scale"
      placement="center"
      role="dialog"
      scrollBehavior="outside"
      size="lg"
      trapFocus
      open
    >
      <ModalBackdrop />
      <ModalPositioner>
        <ModalContent>
          <ModalHeader px="24px" pt="24px" pb="0">
            <ModalTitle fontSize="24px" fontWeight="semibold" mb={1}>
              Set a custom domain
            </ModalTitle>
          </ModalHeader>
          <ModalBody pb="4">
            <Text>
              Enter your custom domain URL below. Verification of your new
              domain may take up to 72 hours, depending on your DNS provider.
            </Text>
            <Field.Root
              invalid={showErrorMessage}
              maxW="sm"
              orientation="vertical"
            >
              <Field.Label fontWeight="semibold">Custom URL</Field.Label>
              <Input
                placeholder="http://www.yoursite.com/customsubdomain/"
                value={domain}
                onChange={(e) => {
                  setDomain(e.target.value.trim());
                  if (showError) setShowError(false);
                  if (requestError) setRequestError(null);
                }}
                onBlur={() => {
                  if (domain.trim().length > 0) {
                    setShowError(true);
                  }
                }}
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    isDomainValid &&
                    !isLoading &&
                    !isInvalid
                  ) {
                    e.preventDefault();
                    handleSubmit(domain);
                  }
                }}
                disabled={isLoading}
                data-testid="customDomain-input-popup.input"
              />
              {showErrorMessage && (
                <Field.ErrorText>
                  {requestError || "Must be a valid URL"}
                </Field.ErrorText>
              )}
            </Field.Root>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
              loading={isLoading}
              data-testid="customDomain-input-popup-cancel.btn"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleSubmit(domain)}
              disabled={!isDomainValid || isInvalid}
              loading={isLoading}
              data-testid="customDomain-input-popup-submit.btn"
            >
              Submit
            </Button>
          </ModalFooter>
          <ModalCloseTrigger asChild>
            <IconButton
              onClick={onClose}
              size="xl"
              label="Close"
              variant="ghost"
              disabled={isLoading}
              loading={isLoading}
            >
              <XIcon />
            </IconButton>
          </ModalCloseTrigger>
        </ModalContent>
      </ModalPositioner>
    </Modal>
  );
};

export default CustomDomainInputPopup;
